import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, ExternalLinkIcon, InfoIcon, TargetIcon } from 'lucide-react';
import { clientCases, getClientCaseBySlug } from '../data/allClientCases';
import { buildBreadcrumbList, buildCaseStudySchema } from '../lib/structuredData';
export const ClientCasePage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const clientCase = getClientCaseBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!clientCase) {
    return <Navigate to="/" replace data-id="element-1090" data-mp="396k7ffxkb481" />;
  }
  const relatedCases = clientCases.filter(c => c.slug !== clientCase.slug).slice(0, 3);
  const renderContentBlock = (block: any, index: number) => {
    switch (block.type) {
      case 'heading':
        return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-6" data-id="element-1091" data-mp="plvxmmcqaesq">
            {block.content}
          </h2>;
      case 'heading3':
        return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-8 mb-4" data-id="element-1092" data-mp="yjxv4rx96pb9">
            {block.content}
          </h3>;
      case 'paragraph':
        return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-1093" data-mp="18hvrk2p9nsey">
            {block.content}
          </p>;
      case 'list':
        return <ul key={index} className="space-y-3 mb-8 ml-6 list-disc text-lg text-[#4B5563]" data-id="element-1094" data-mp="1hecuiinh8jzm">
            {block.items?.map((item: string, i: number) => <li key={i} className="pl-2 leading-relaxed" data-id="element-1095" data-mp="1r83hfj3ygmd7">
                {item}
              </li>)}
          </ul>;
      case 'callout':
        return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#007AFF] p-6 rounded-r-lg my-8" data-id="element-1096" data-mp="16lnei5ootrpm">
            <div className="flex items-start gap-4" data-id="element-1097" data-mp="1gaykhjga2g9a">
              <InfoIcon className="w-6 h-6 text-[#007AFF] flex-shrink-0 mt-1" data-id="element-1098" data-mp="1gchx902q696b" />
              <p className="text-lg text-[#1B2B44] font-medium leading-relaxed m-0" data-id="element-1099" data-mp="1bj705qnpt6rl">
                {block.content}
              </p>
            </div>
          </div>;
      case 'formula':
        return <div key={index} className="bg-[#1B2B44] text-white p-8 rounded-xl my-8 text-center shadow-lg" data-id="element-1100" data-mp="1kc0ijwygkm9o">
            <span className="text-sm text-white/60 uppercase tracking-wider font-semibold mb-3 block" data-id="element-1101" data-mp="1uaq1zu606es1">
              De Formule
            </span>
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-1102" data-mp="4wkxegtcm4ub">
              {block.content}
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full min-h-screen bg-white font-sans" data-id="element-1103" data-mp="2bl3ik0xy0u1d">
      <SEO title={`${clientCase.clientName} | Klantcase Noord Digital`} description={clientCase.metaDescription} canonical={`https://noorddigital.nl/cases/${clientCase.slug}`} keywords={`${clientCase.clientName}, ${clientCase.sector}, klantcase, website laten maken ${clientCase.sector.toLowerCase()}, Noord Digital`} ogType="article" ogImage={clientCase.heroImage} jsonLd={[buildCaseStudySchema({
      clientName: clientCase.clientName,
      description: clientCase.metaDescription,
      path: `/cases/${clientCase.slug}`,
      sector: clientCase.sector,
      image: clientCase.heroImage,
      clientWebsite: clientCase.website
    }), buildBreadcrumbList([{
      name: 'Cases',
      path: '/#client-cases'
    }, {
      name: clientCase.clientName,
      path: `/cases/${clientCase.slug}`
    }])]} data-id="element-1104" data-mp="2givj1jcnloh6" />
      <Header data-id="element-1105" data-mp="1d87kky65uh2u" />

      {/* Breadcrumb & Hero */}
      <div className="bg-[#F7F8FA] pt-24 pb-16 border-b border-gray-200" data-id="element-1106" data-mp="1d5bm98qehqdv">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1107" data-mp="193q69eikkvzg">
          <nav className="flex items-center text-sm text-[#6B7280] mb-8" data-id="element-1108" data-mp="145on6tl58wnz">
            <Link to="/" className="hover:text-[#007AFF] transition-colors" data-id="element-1109" data-mp="z7n3c6fw8tbg">
              Home
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1110" data-mp="uflt0q2adco2" />
            <Link to="/bibliotheek" className="hover:text-[#007AFF] transition-colors" data-id="element-1111" data-mp="phk3pne9oh45">
              Bibliotheek
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1112" data-mp="ldvtewy937f7" />
            <span className="text-[#111827] font-medium truncate" data-id="element-1113" data-mp="gh5hmlthaxzl">
              {clientCase.clientName}
            </span>
          </nav>

          <div className="max-w-3xl" data-id="element-1114" data-mp="bjvsl7a04oca">
            <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-semibold rounded-full mb-6" data-id="element-1115" data-mp="2n5nvdkvqukwm">
              {clientCase.sector}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] leading-tight mb-6" data-id="element-1116" data-mp="2s627be0guezx">
              {clientCase.clientName}
            </h1>
            <p className="text-xl text-[#6B7280] leading-relaxed" data-id="element-1117" data-mp="313w0wh6uiuje">
              {clientCase.shortDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content & Sidebar */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-16" data-id="element-1118" data-mp="29ej3j4ymawwu">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1119" data-mp="2df2xjznw9ker">
          {/* Article Content */}
          <div className="lg:col-span-8" data-id="element-1120" data-mp="2i6bctcbwnzzu">
            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg" data-id="element-1121" data-mp="2i85vejzpfg0r">
              <img src={clientCase.heroImage} alt={`${clientCase.clientName} case study`} className="w-full h-auto object-cover object-top max-h-[500px]" data-id="element-1122" data-mp="1lndlcj57fsjd" />
            </div>
            <article className="prose prose-lg max-w-none prose-headings:text-[#111827] prose-p:text-[#4B5563] prose-a:text-[#007AFF]" data-id="element-1123" data-mp="2sybfqg92atsi">
              {clientCase.content.map((block, index) => renderContentBlock(block, index))}
            </article>

            {/* Link naar de live website van de klant */}
            {clientCase.website && <a href={clientCase.website} target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center gap-2 bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-[#005FCC] transition-colors" data-id="element-1124" data-mp="2a222tw9wc9a3">
                Bekijk de website van {clientCase.clientName}
                <ExternalLinkIcon className="w-4 h-4" data-id="element-1125" data-mp="3ugc9im4cytd9" />
              </a>}

            {/* Author / Bio block */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex items-center gap-6" data-id="element-1126" data-mp="3qi4o3dyy93vq">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full bg-[#F7F8FA] border border-gray-200" loading="lazy" data-id="element-1127" data-mp="1svoqxonf7wx" />
              <div data-id="element-1128" data-mp="3t0qacwmcl5vu">
                <h4 className="font-bold text-[#111827]" data-id="element-1129" data-mp="3t2b2ygp3qer2">Norden</h4>
                <p className="text-[#6B7280] text-sm" data-id="element-1130" data-mp="kl6le9spm9ml">
                  Klantcases bij Noord Digital
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-1131" data-mp="aubvmvg98rwe">
            <div className="sticky top-24 space-y-8" data-id="element-1132" data-mp="fty7b0gfq033">
              {/* Back to Bibliotheek */}
              <Link to="/bibliotheek" className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-[#F7F8FA] hover:bg-gray-100 text-[#1B2B44] font-semibold rounded-xl transition-colors border border-gray-200" data-id="element-1133" data-mp="13jivbxrg3nj8">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-1134" data-mp="34eoucm0s36cy" />
                Terug naar Bibliotheek
              </Link>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] rounded-2xl p-8 text-white shadow-xl" data-id="element-1135" data-mp="2vktw7xy2qg6t">
                <h3 className="text-xl font-bold mb-3" data-id="element-1136" data-mp="2qms6iu657imd">
                  Ook zulke resultaten behalen?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6" data-id="element-1137" data-mp="3nbq0ia62jcih">
                  Ontdek wat we voor jouw bedrijf kunnen betekenen. Vraag een
                  gratis scan aan en we laten je direct zien waar je kansen
                  liggen.
                </p>
                <a href="/prijsscan" className="block w-full py-3 px-4 bg-white text-[#007AFF] text-center font-bold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1138" data-mp="39d9xat7f0453">
                  Start de SEO scan
                </a>
              </div>

              {/* Related Cases */}
              {relatedCases.length > 0 && <div className="bg-white border border-gray-200 rounded-2xl p-6" data-id="element-1139" data-mp="1ss7xknd3pk5t">
                  <h3 className="text-lg font-bold text-[#111827] mb-4 flex items-center gap-2" data-id="element-1140" data-mp="1sq523nt6iytp">
                    <TargetIcon className="w-5 h-5 text-[#007AFF]" data-id="element-1141" data-mp="1xpq11y8cn76f" />
                    Andere cases
                  </h3>
                  <ul className="space-y-3" data-id="element-1142" data-mp="26ke21c6w62pf">
                    {relatedCases.map((relCase, idx) => <li key={idx} data-id="element-1143" data-mp="2bi65n3gwfd9g" data-mpi={idx}>
                        <Link to={`/cases/${relCase.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug" data-id="element-1144" data-mp="2bheblx2y9nla">
                          <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" data-id="element-1145" data-mp="m4as7h10zgj0" />
                          {relCase.clientName}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* More Cases Grid */}
      {relatedCases.length > 0 && <div className="bg-[#F7F8FA] py-20 border-t border-gray-200" data-id="element-1146" data-mp="foqmg39kqyf5">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1147" data-mp="frl3rlxak34c">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-10 text-center" data-id="element-1148" data-mp="km8kuzpytv8w">
              Meer succesverhalen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1149" data-mp="ulzktgxap2gd">
              {relatedCases.map((relCase, idx) => <Link key={idx} to={`/cases/${relCase.slug}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#007AFF]/30 transition-all group" data-id="element-1150" data-mp="zeivo64lljhh" data-mpi={idx}>
                  <div className="h-40 mb-4 overflow-hidden rounded-lg" data-id="element-1151" data-mp="1dcqz8ww53j1m">
                    <img src={relCase.heroImage} alt={relCase.clientName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-1152" data-mp="1dawdxhgyhox0" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1B2B44] mb-3 group-hover:text-[#007AFF] transition-colors" data-id="element-1153" data-mp="19h625cg2y29l">
                    {relCase.clientName}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed mb-4 line-clamp-3" data-id="element-1154" data-mp="269jblwdld77n">
                    {relCase.shortDescription}
                  </p>
                  <span className="text-[#007AFF] text-sm font-semibold flex items-center gap-1" data-id="element-1155" data-mp="1wgketr7xyacy">
                    Lees de case <ArrowRightIcon className="w-4 h-4" data-id="element-1156" data-mp="1sfinrxo6h4m2" />
                  </span>
                </Link>)}
            </div>
          </div>
        </div>}

      <Footer data-id="element-1157" data-mp="2k6y63fipc56h" />
    </div>;
};